import MuiTextField from '@mui/material/TextField';
import { type ChangeEvent, type JSX, useCallback } from 'react';
import { type FieldPath, type FieldPathValue, type FieldValues, useController } from 'react-hook-form';
import { useDesignSystemEnabled } from '../theme/useDesignSystemEnabled.ts';
import { DesignSystemTextField } from './textFieldDesignSystem.tsx';
import type { RHFTextFieldProps } from './type.tsx';

export const RHFTextField = <T extends FieldValues = Record<string, string>>({
  name,
  rules,
  onChange,
  onChangeComplete,
  control = undefined,
  dataTest = undefined,
  formatter = value => value,
  parser = value => value,
  valueOverride = undefined,
  designSystem,
  ...rest
}: RHFTextFieldProps<T>): JSX.Element => {
  const isDesignSystem = useDesignSystemEnabled('textField', designSystem);
  const {
    field: { value, onChange: fieldOnChange, ...restFieldProps },
    fieldState,
    formState: { isSubmitting },
  } = useController({
    name,
    rules,
    ...(control ? { control } : {}),
  });

  const onInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const nextValue = parser(event.target.value as unknown as FieldPathValue<T, FieldPath<T>>);

      onChange?.(nextValue, event);

      if (event.isDefaultPrevented()) {
        return;
      }
      fieldOnChange(nextValue);

      onChangeComplete?.(nextValue);
    },
    [fieldOnChange, onChange, onChangeComplete, parser]
  );

  const formattedValue = formatter((value ?? '') as FieldPathValue<T, FieldPath<T>>);
  const safeValue = valueOverride ?? formattedValue;

  const fieldProps = {
    value: safeValue ?? '',
    onChange: onInputChange,
    error: fieldState.invalid || rest.error,
    disabled: isSubmitting || rest.disabled,
    helperText: fieldState.error?.message ?? rest.helperText,
  };

  if (isDesignSystem) {
    return (
      <DesignSystemTextField dataTest={rest['data-test'] ?? dataTest} {...rest} {...restFieldProps} {...fieldProps} />
    );
  }

  return (
    <MuiTextField
      data-test={dataTest}
      variant="standard"
      {...rest}
      {...restFieldProps}
      slotProps={{
        ...(rest.variant !== 'outlined' && { input: { disableUnderline: true } }),
        inputLabel: { disableAnimation: true, focused: false, shrink: true },
        ...rest.slotProps,
      }}
      {...fieldProps}
    />
  );
};
